import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, List, Skeleton, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import { listNotificationInbox } from '@/services/notificationInboxService';
import { t } from '@/utils/language';
import { useLanguage } from '@/context/lang/languageContext';

const { Text } = Typography;

const PRIORITY_COLORS = {
  URGENT: '#cf1322',
  HIGH: '#ff4d4f',
  NORMAL: '#1677ff',
  LOW: '#1890ff',
};

const DEFAULT_LIMIT = 10;
const MAX_DISPLAY_ITEMS = 5;

const NotificationPopover = ({ open, onViewAll, onRefreshUnreadCount }) => {
  const { currentLang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const priorityLabelMap = useMemo(
    () => ({
      URGENT: t('notifications.priority.urgent'),
      HIGH: t('notifications.priority.high'),
      NORMAL: t('notifications.priority.normal'),
      LOW: t('notifications.priority.low'),
    }),
    [currentLang]
  );

  const typeLabelMap = useMemo(
    () => ({
      GENERAL: t('notifications.type.general'),
      SYSTEM: t('notifications.type.system'),
      BOOKING: t('notifications.type.booking'),
      PAYMENT: t('notifications.type.payment'),
      TASK: t('notifications.type.task'),
      PROMO: t('notifications.type.promo'),
    }),
    [currentLang]
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listNotificationInbox({
        limit: DEFAULT_LIMIT,
        unreadOnly: true,
      });
      setNotifications(response?.items || response?.arrList || []);
    } catch (error) {
      console.error('Failed to fetch unread notifications', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      if (typeof onRefreshUnreadCount === 'function') {
        onRefreshUnreadCount();
      }
    }
  }, [open, fetchNotifications, onRefreshUnreadCount]);

  const renderNotification = (item) => {
    const priority = (item?.priority || item?.strPriority || '').toUpperCase();
    const createdAtRaw = item?.createdAt || item?.strCreatedTime;
    const createdAt = createdAtRaw
      ? dayjs(createdAtRaw).format('YYYY-MM-DD HH:mm')
      : '';
    const title = item?.title || item?.strTitle;
    const content = item?.content || item?.strDescription;
    const type = (item?.category || item?.strType || '').toUpperCase();

    return (
      <List.Item key={item?._id || Math.random().toString(36)}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space align="start" size={8} style={{ justifyContent: 'space-between' }}>
            <Text strong ellipsis={{ tooltip: title }}>{title || '-'}</Text>
            {priority ? (
              <Tag color={PRIORITY_COLORS[priority] || 'blue'}>
                {priorityLabelMap[priority] || priority}
              </Tag>
            ) : null}
          </Space>
          <Text type="secondary">
            {item?.timeAgo || ''}
            {createdAt ? ` • ${createdAt}` : ''}
            {type ? ` • ${typeLabelMap[type] || type}` : ''}
          </Text>
          {content ? (
            <Text type="secondary" ellipsis={{ tooltip: content }}>
              {content}
            </Text>
          ) : null}
        </Space>
      </List.Item>
    );
  };

  return (
    <div className="notification-popover">
      {loading ? (
        <div style={{ width: 320 }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </div>
      ) : notifications.length === 0 ? (
        <Empty description={t('notifications.drawer.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={notifications.slice(0, MAX_DISPLAY_ITEMS)}
          renderItem={renderNotification}
          style={{ width: 360 }}
          itemLayout="vertical"
        />
      )}
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Button type="link" onClick={onViewAll}>
          {t('navigation.notificationCenter')}
        </Button>
      </div>
    </div>
  );
};

export default NotificationPopover;
